interface CountryCode {
  code: string;
  name: string;
}

/**
 * Countries available in Outdoorsy
 */
const countries: Array<CountryCode> = [
  {
    code: 'AU',
    name: 'Australia',
  },
  {
    code: 'AT',
    name: 'Austria',
  },
  {
    code: 'BE',
    name: 'Belgium',
  },
  {
    code: 'CA',
    name: 'Canada',
  },
  {
    code: 'HR',
    name: 'Croatia',
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
  },
  {
    code: 'FR',
    name: 'France',
  },
  {
    code: 'DE',
    name: 'Germany',
  },
  {
    code: 'GR',
    name: 'Greece',
  },
  {
    code: 'HU',
    name: 'Hungary',
  },
  {
    code: 'IS',
    name: 'Iceland',
  },
  {
    code: 'IE',
    name: 'Ireland',
  },
  {
    code: 'IM',
    name: 'Isle of Man',
  },
  {
    code: 'IT',
    name: 'Italy',
  },
  {
    code: 'LI',
    name: 'Liechtenstein',
  },
  {
    code: 'LU',
    name: 'Luxembourg',
  },
  {
    code: 'MT',
    name: 'Malta',
  },
  {
    code: 'MX',
    name: 'Mexico',
  },
  {
    code: 'ME',
    name: 'Montenegro',
  },
  {
    code: 'NZ',
    name: 'New Zealand',
  },
  {
    code: 'SM',
    name: 'San Marino',
  },
  {
    code: 'SI',
    name: 'Slovenia',
  },
  {
    code: 'ES',
    name: 'Spain',
  },
  {
    code: 'CH',
    name: 'Switzerland',
  },
  {
    code: 'GB',
    name: 'United Kingdom',
  },
  {
    code: 'US',
    name: 'United States',
  },
];

export const countryCodes = countries.map(country => country.code);

// Geocoders localize the country to the Guest's browser language.
const countryAliases: Record<string, string[]> = {
  US: [
    'USA',
    'United States of America',
    'Estados Unidos',
    'Estados Unidos de América',
    'Estados Unidos da América',
    'États-Unis',
    "États-Unis d'Amérique",
    'Vereinigte Staaten',
    'Vereinigte Staaten von Amerika',
    'Stati Uniti',
    "Stati Uniti d'America",
    'Verenigde Staten',
    'Egyesült Államok',
    'Сполучені Штати Америки',
    'Соединённые Штаты Америки',
    'США',
    'ארצות הברית',
    '美国',
    '美國',
    '미국',
    '미 합중국',
    'アメリカ合衆国',
  ],
  CA: ['Kanada', '加拿大'],
};

const normalizeCountry = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();

const countryCodeLookup = new Map<string, string>(
  countries.flatMap(({ code, name }) =>
    [code, name, ...(countryAliases[code] ?? [])].map(
      spelling => [normalizeCountry(spelling), code] as const,
    ),
  ),
);

/** Maps a stored country code, name or alias to its alpha-2 code, or undefined if unrecognized. */
export const toCountryCode = (value?: string | null): string | undefined => {
  if (!value) return undefined;

  const normalized = normalizeCountry(value);
  const exact = countryCodeLookup.get(normalized);
  if (exact || !normalized.includes(',')) return exact;

  const lastSegment = normalized.slice(normalized.lastIndexOf(',') + 1).trim();
  // Other two-letter segments are states or provinces ("Portland, ME"); no state is "US".
  const isCountrySegment = lastSegment.length > 2 || lastSegment === 'us';
  return isCountrySegment ? countryCodeLookup.get(lastSegment) : undefined;
};
